import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type {
  FlowEvent,
  FlowSummary,
  FlowTrigger,
} from "@care-by-ella/contracts";
import { TRIGGER_EVENTS } from "@/lib/flow-event-labels";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import { listFlows } from "@/lib/flows-api";
import {
  archiveJourney,
  createJourney,
  fetchJourney,
  publishJourney,
  saveJourneyDraft,
} from "@/lib/journeys-api";

// TES-224 — the journey builder. A journey is a name + an ordered set of member
// flow keys. Mirrors the FlowEditor draft/publish/version UX. Publishing writes
// membership through to the flows' journeyKey (server-side).

type EditableJourney = {
  name: string;
  description: string;
  memberFlowKeys: string[];
  // How patients enroll: manual attach from the patient page, or
  // automatically when a domain event fires (onEvent enrollment).
  trigger: FlowTrigger;
};

export function JourneyEditorRoute() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { key: routeKey } = useParams<{ key: string }>();
  const isNew = !routeKey || routeKey === "new";

  const [keyInput, setKeyInput] = useState("");
  const [draft, setDraft] = useState<EditableJourney | null>(null);
  const [flows, setFlows] = useState<FlowSummary[]>([]);
  const [publishedVersion, setPublishedVersion] = useState<number | null>(null);
  const [hadDraft, setHadDraft] = useState(false);
  const [busy, setBusy] = useState<"save" | "publish" | "archive" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handleErr = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    },
    [signOut],
  );

  const load = useCallback(() => {
    setError(null);
    // Published flows are the pool of selectable members.
    listFlows()
      .then((rows) => setFlows(rows.filter((r) => r.status === "published")))
      .catch(handleErr);

    if (isNew) {
      setDraft({
        name: "",
        description: "",
        memberFlowKeys: [],
        trigger: { type: "manual" },
      });
      return;
    }
    fetchJourney(routeKey!)
      .then((view) => {
        const base = view.draft ?? view.published;
        setPublishedVersion(view.published?.version ?? null);
        setHadDraft(Boolean(view.draft));
        setDraft(
          base
            ? {
                name: base.name,
                description: base.description ?? "",
                memberFlowKeys: base.memberFlowKeys,
                trigger: base.trigger ?? { type: "manual" },
              }
            : {
                name: "",
                description: "",
                memberFlowKeys: [],
                trigger: { type: "manual" },
              },
        );
      })
      .catch(handleErr);
  }, [isNew, routeKey, handleErr]);

  useEffect(load, [load]);

  function patch(p: Partial<EditableJourney>) {
    setDraft((d) => (d ? { ...d, ...p } : d));
  }

  function toggleMember(flowKey: string, on: boolean) {
    setDraft((d) => {
      if (!d) return d;
      const set = d.memberFlowKeys.filter((k) => k !== flowKey);
      return {
        ...d,
        memberFlowKeys: on ? [...set, flowKey] : set,
      };
    });
  }

  function move(index: number, dir: -1 | 1) {
    setDraft((d) => {
      if (!d) return d;
      const keys = d.memberFlowKeys.slice();
      const j = index + dir;
      if (j < 0 || j >= keys.length) return d;
      [keys[index], keys[j]] = [keys[j]!, keys[index]!];
      return { ...d, memberFlowKeys: keys };
    });
  }

  function payload() {
    if (!draft) throw new Error("no draft");
    return {
      name: draft.name,
      description: draft.description || undefined,
      memberFlowKeys: draft.memberFlowKeys,
      trigger: draft.trigger,
    };
  }

  async function onSave() {
    if (!draft) return;
    setBusy("save");
    setError(null);
    setNotice(null);
    try {
      if (isNew) {
        const created = await createJourney({ key: keyInput, ...payload() });
        setNotice("Resa skapad.");
        navigate(`/journeys/${encodeURIComponent(created.key)}`);
        return;
      }
      await saveJourneyDraft(routeKey!, payload());
      setHadDraft(true);
      setNotice("Utkast sparat.");
    } catch (e) {
      handleErr(e);
    } finally {
      setBusy(null);
    }
  }

  async function onPublish() {
    if (isNew || !routeKey) return;
    setBusy("publish");
    setError(null);
    setNotice(null);
    try {
      const pub = await publishJourney(routeKey);
      setPublishedVersion(pub.version);
      setHadDraft(false);
      setNotice(`Publicerad — version ${pub.version} är nu aktiv.`);
    } catch (e) {
      handleErr(e);
    } finally {
      setBusy(null);
    }
  }

  async function onArchive() {
    if (isNew || !routeKey) return;
    setBusy("archive");
    setError(null);
    try {
      await archiveJourney(routeKey);
      navigate("/journeys");
    } catch (e) {
      handleErr(e);
    } finally {
      setBusy(null);
    }
  }

  if (!draft) {
    return <div className="text-textSecondary text-sm">Hämtar…</div>;
  }

  const flowName = (k: string) => flows.find((f) => f.key === k)?.name ?? k;
  const nonMembers = flows.filter((f) => !draft.memberFlowKeys.includes(f.key));

  return (
    <div className="flex flex-col gap-md max-w-3xl">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl tracking-tight">
          {isNew ? "Ny resa" : draft.name || routeKey}
        </h1>
        <div className="flex items-center gap-sm">
          {!isNew && publishedVersion ? (
            <span className="text-xs text-textSecondary">
              Publicerad v{publishedVersion}
              {hadDraft ? " · utkast ej publicerat" : ""}
            </span>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-sm text-sm">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="bg-success/10 border border-success/30 text-success rounded-md p-sm text-sm">
          {notice}
        </div>
      ) : null}

      <div className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md">
        {isNew ? (
          <label className="flex flex-col gap-xs">
            <span className="text-xs uppercase tracking-wide text-textSecondary">
              Nyckel (slug)
            </span>
            <input
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="t.ex. onboarding"
              className="border border-surfaceMuted rounded-md p-sm text-sm font-mono focus:outline-none focus:border-brand"
            />
          </label>
        ) : null}

        <label className="flex flex-col gap-xs">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Namn
          </span>
          <input
            value={draft.name}
            onChange={(e) => patch({ name: e.target.value })}
            className="border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand"
          />
        </label>

        <label className="flex flex-col gap-xs">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Beskrivning
          </span>
          <textarea
            value={draft.description}
            onChange={(e) => patch({ description: e.target.value })}
            className="border border-surfaceMuted rounded-md p-sm text-sm min-h-[60px] focus:outline-none focus:border-brand"
          />
        </label>

        {/* Enrollment trigger — mirrors the FlowEditor picker. 'Händelse'
            enrolls the patient automatically when the event fires. */}
        <div className="grid grid-cols-2 gap-md">
          <label className="flex flex-col gap-xs">
            <span className="text-xs uppercase tracking-wide text-textSecondary">
              Tilldelning
            </span>
            <select
              value={draft.trigger.type}
              onChange={(e) =>
                patch({
                  trigger:
                    e.target.value === "event"
                      ? {
                          type: "event",
                          event: draft.trigger.event ?? TRIGGER_EVENTS[0]!.key,
                        }
                      : { type: "manual" },
                })
              }
              className="border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand"
            >
              <option value="manual">Manuell (kliniker tilldelar)</option>
              <option value="event">Händelse (automatisk)</option>
            </select>
          </label>
          {draft.trigger.type === "event" ? (
            <label className="flex flex-col gap-xs">
              <span className="text-xs uppercase tracking-wide text-textSecondary">
                Händelse
              </span>
              <select
                value={draft.trigger.event ?? TRIGGER_EVENTS[0]!.key}
                onChange={(e) =>
                  patch({
                    trigger: {
                      type: "event",
                      event: e.target.value as FlowEvent,
                    },
                  })
                }
                className="border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand"
              >
                {TRIGGER_EVENTS.map((ev) => (
                  <option key={ev.key} value={ev.key}>
                    {ev.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </div>

      {/* Member flows — ordered */}
      <div className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-sm">
        <h2 className="font-display text-lg">Flöden i resan</h2>
        {draft.memberFlowKeys.length === 0 ? (
          <p className="text-textSecondary text-sm">
            Inga flöden valda än. Lägg till nedan.
          </p>
        ) : (
          <ol className="flex flex-col gap-xs">
            {draft.memberFlowKeys.map((k, i) => (
              <li
                key={k}
                className="flex items-center justify-between border border-surfaceMuted rounded-md px-sm py-xs text-sm"
              >
                <span>
                  <span className="text-textSecondary tabular-nums mr-sm">
                    {i + 1}.
                  </span>
                  {flowName(k)}{" "}
                  <span className="font-mono text-xs text-textSecondary">
                    ({k})
                  </span>
                </span>
                <span className="flex items-center gap-xs">
                  <button
                    type="button"
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    className="text-textSecondary hover:text-textPrimary disabled:opacity-30"
                    aria-label="Flytta upp"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, 1)}
                    disabled={i === draft.memberFlowKeys.length - 1}
                    className="text-textSecondary hover:text-textPrimary disabled:opacity-30"
                    aria-label="Flytta ner"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleMember(k, false)}
                    className="text-textSecondary hover:text-danger"
                  >
                    Ta bort
                  </button>
                </span>
              </li>
            ))}
          </ol>
        )}

        {nonMembers.length > 0 ? (
          <div className="flex items-center gap-sm pt-xs">
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) toggleMember(e.target.value, true);
              }}
              className="rounded-md border border-surfaceMuted bg-surface px-sm py-xs text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
            >
              <option value="">Lägg till flöde…</option>
              {nonMembers.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.name} ({f.key})
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      <div className="flex items-center gap-sm">
        <button
          type="button"
          onClick={onSave}
          disabled={busy !== null || (isNew && !keyInput)}
          className="px-md py-sm rounded-md bg-brand text-white text-sm hover:bg-brand/90 disabled:opacity-50"
        >
          {busy === "save" ? "Sparar…" : isNew ? "Skapa" : "Spara utkast"}
        </button>
        {!isNew ? (
          <>
            <button
              type="button"
              onClick={onPublish}
              disabled={busy !== null}
              className="px-md py-sm rounded-md border border-brand text-brand text-sm hover:bg-brand/5 disabled:opacity-50"
            >
              {busy === "publish" ? "Publicerar…" : "Publicera"}
            </button>
            <button
              type="button"
              onClick={onArchive}
              disabled={busy !== null}
              className="ml-auto px-md py-sm rounded-md border border-surfaceMuted text-textSecondary text-sm hover:text-danger hover:border-danger disabled:opacity-50"
            >
              Arkivera
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
